import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { FirebaseAuthService } from '@/cloud/services/firebase-auth.service';
import { Message } from 'primeng/message';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ButtonModule, CheckboxModule, InputTextModule, PasswordModule, FormsModule, RouterModule, RippleModule, Message],
    styles: [
        `
            :host {
                display: block;
            }
            .login-bg-wrapper {
                position: relative;
            }
            .login-bg-video {
                position: absolute;
                inset: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
                z-index: 0;
                filter: brightness(0.5) saturate(1.2);
            }
            .login-card {
                backdrop-filter: blur(10px);
            }
            @media (max-width: 768px) {
                .login-bg-video {
                    filter: brightness(0.4);
                }
            }
        `
    ],
    template: `
        <div class="login-bg-wrapper bg-surface-950 flex items-center justify-center min-h-screen min-w-screen overflow-hidden">
            <video class="login-bg-video" autoplay muted playsinline loop preload="auto" poster="/video/proav-home-banner-poster.jpg">
                <source src="video/proav-home-banner.mp4" type="video/mp4" />
            </video>
            <div class="cloud-login-container flex flex-col items-center justify-center relative z-10">
                <div style="border-radius: 56px; padding: 0.3rem;" class="login-card">
                    <div class="w-full py-20 px-8 sm:px-20 flex flex-col gap-4" style="border-radius: 53px; background: color-mix(in srgb, var(--p-surface-950) 55%, transparent);">
                        <div class="flex flex-col items-center text-center mb-2">
                            <svg width="165" height="38.343" style="margin-bottom: 1rem;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 165 38.343">
                                <g transform="translate(-192.662 -398.538)">
                                    <g transform="translate(283.839 398.538)">
                                        <path
                                            d="M305.921,428.649l13.742-23.8c.072-.121.139-.217.195-.294a2.422,2.422,0,0,1,.2.3l13.743,23.8h6.72L325.1,401.935a5.736,5.736,0,0,0-10.475,0L299.2,428.649Z"
                                            transform="translate(-299.199 -398.538)"
                                            style="fill: var(--p-primary-color, #7ab800)"
                                        />
                                    </g>
                                    <g transform="translate(316.34 399.47)">
                                        <path
                                            d="M371.776,399.628l-13.744,23.8a2.8,2.8,0,0,1-.195.3,2.782,2.782,0,0,1-.2-.3l-13.743-23.8h-6.72L352.6,426.342a5.736,5.736,0,0,0,10.475,0L378.5,399.628Z"
                                            transform="translate(-337.176 -399.628)"
                                            style="fill: var(--p-primary-color, #7ab800)"
                                        />
                                    </g>
                                    <g transform="translate(192.662 398.671)">
                                        <path
                                            d="M198.483,437.037h-5.821V409.521a9.911,9.911,0,0,1,9.9-9.9h8.053a14.608,14.608,0,0,1,0,29.216h-8.538v-5.826h8.538a8.782,8.782,0,0,0,0-17.564h-8.053a4.081,4.081,0,0,0-4.075,4.078v27.516Z"
                                            transform="translate(-192.662 -398.828)"
                                            fill="#868686"
                                        />
                                        <g transform="translate(35.513 0.789)">
                                            <path
                                                d="M259.588,402.722a14.5,14.5,0,0,0-9-3.1h-6.5a9.909,9.909,0,0,0-9.9,9.9V428.8h5.821V409.515a4.081,4.081,0,0,1,4.076-4.075h6.5a8.714,8.714,0,0,1,5.979,2.363A18.432,18.432,0,0,1,259.588,402.722Z"
                                                transform="translate(-234.193 -399.617)"
                                                fill="#868686"
                                            />
                                        </g>
                                        <path
                                            d="M279.622,429.48c-8.972,0-16.268-6.906-16.268-15.392s7.3-15.393,16.268-15.393,16.27,6.9,16.27,15.393-7.3,15.392-16.27,15.392Zm0-24.965c-5.76,0-10.446,4.294-10.446,9.573s4.686,9.573,10.446,9.573,10.448-4.3,10.448-9.573-4.688-9.573-10.448-9.573Z"
                                            transform="translate(-202.906 -398.694)"
                                            fill="#868686"
                                        />
                                    </g>
                                </g>
                            </svg>
                            <span class="font-medium">{{ isEmailLinkFlow() ? 'Complete your sign-up' : mode() === 'login' ? 'Sign in to continue' : 'Reset your password' }}</span>
                        </div>
                        @if ((cloud.authError() || firebaseAuth.firebaseError()) && mode() === 'login') {
                            <p-message severity="error">{{ cloud.authError() || firebaseAuth.firebaseError() }}</p-message>
                        }
                        @if (forgotSubmitted() && mode() === 'forgot') {
                            <p-message severity="info">If an account exists for that email, a reset link has been sent.</p-message>
                        }
                        @if (isEmailLinkFlow() && loginStep() === 'email') {
                            <p-message severity="info">Please enter your email address to complete sign-up.</p-message>
                        }
                        <form (ngSubmit)="onSubmit()" novalidate>
                            @if (mode() === 'login' && loginStep() === 'email') {
                                <!-- Step 1: Email Entry (or email link sign-up) -->
                                @if (firebaseAuth.isEmailEnabled() || isEmailLinkFlow()) {
                                    <label for="email1" class="block text-surface-0 text-xl font-medium mb-2">Email Address</label>
                                    <input pInputText id="email1" name="email" type="text" placeholder="Enter your email address" class="w-full mb-8" [(ngModel)]="email" (keyup.enter)="onSubmit()" autofocus />
                                    <p-button [label]="isEmailLinkFlow() ? 'Sign Up' : 'Continue'" [fluid]="true" type="submit" [loading]="loading()" [disabled]="loading() || !email().trim()"></p-button>
                                }

                                @if (hasSocialProviders() && !isEmailLinkFlow()) {
                                    @if (firebaseAuth.isEmailEnabled()) {
                                        <div class="flex items-center gap-4 my-6">
                                            <div class="flex-1 border-t border-surface-300"></div>
                                            <span class="text-surface-400 text-sm">or sign in with</span>
                                            <div class="flex-1 border-t border-surface-300"></div>
                                        </div>
                                    }

                                    <div class="flex gap-3">
                                        @if (firebaseAuth.isMicrosoftEnabled()) {
                                            <button type="button" pRipple class="social-btn" (click)="loginWithMicrosoft()" [disabled]="loading()">
                                                <svg class="social-btn-logo" viewBox="0 0 21 21" aria-hidden="true">
                                                    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                                                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                                                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                                                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                                                </svg>
                                                <span>Microsoft</span>
                                            </button>
                                        }
                                        @if (firebaseAuth.isGoogleEnabled()) {
                                            <button type="button" pRipple class="social-btn" (click)="loginWithGoogle()" [disabled]="loading()">
                                                <svg class="social-btn-logo" viewBox="0 0 48 48" aria-hidden="true">
                                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                                </svg>
                                                <span>Google</span>
                                            </button>
                                        }
                                    </div>
                                }
                            } @else if (mode() === 'login' && loginStep() === 'password') {
                                <!-- Step 2: Password Entry (for email auth) -->
                                <label for="email1" class="block text-surface-0 text-xl font-medium mb-2">Email Address</label>
                                <div class="flex items-center gap-2 mb-4">
                                    <input pInputText id="email1" name="email" type="text" class="w-full md:w-120" [(ngModel)]="email" [disabled]="true" style="flex: 1;" />
                                    <p-button icon="pi pi-pencil" severity="secondary" [outlined]="true" (onClick)="backToEmailStep()" [disabled]="loading()"></p-button>
                                </div>
                                <label for="password1" class="block text-surface-0 font-medium text-xl mb-2">Password</label>
                                <p-password
                                    class="login-password mb-4"
                                    id="password1"
                                    name="password"
                                    [(ngModel)]="password"
                                    placeholder="Password"
                                    [toggleMask]="true"
                                    [fluid]="true"
                                    [feedback]="false"
                                    (onKeydownEnter)="onSubmit()"
                                    autofocus></p-password>

                                <div class="flex items-center justify-between mt-2 mb-8 gap-8">
                                    <div class="flex items-center">
                                        <p-checkbox [(ngModel)]="checked" id="rememberme1" name="remember" binary class="mr-2"></p-checkbox>
                                        <label for="rememberme1">Remember me</label>
                                    </div>
                                    <span class="font-medium no-underline ml-2 text-right cursor-pointer text-primary" (click)="switchToForgot()">Forgot password?</span>
                                </div>
                                <p-button label="Sign In" [fluid]="true" type="submit" [loading]="loading()" [disabled]="loading()"></p-button>
                            } @else if (mode() === 'forgot') {
                                <!-- Forgot Password Flow -->
                                @if (!forgotSubmitted()) {
                                    <label for="email1" class="block text-surface-0 text-xl font-medium mb-2">Email</label>
                                    <input pInputText id="email1" name="email" type="text" placeholder="Email address" class="w-full md:w-120 mb-8" [(ngModel)]="email" (keyup.enter)="onSubmit()" />
                                    <div class="flex items-center justify-between mt-2 mb-8 gap-8">
                                        <span class="font-medium no-underline ml-2 text-right cursor-pointer text-primary" (click)="backToLogin()">Back to sign in</span>
                                    </div>
                                    <p-button label="Send Reset Link" [fluid]="true" type="submit" [loading]="loading()" [disabled]="loading()"></p-button>
                                } @else {
                                    <p-message severity="info">If an account exists for that email, a reset link has been sent.</p-message>
                                    <div class="mt-4">
                                        <p-button label="Done" [fluid]="true" type="button" (onClick)="backToLogin()"></p-button>
                                    </div>
                                }
                            }
                        </form>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class CloudLogin implements OnInit {
    public cloud = inject(CloudApiService);
    public firebaseAuth = inject(FirebaseAuthService);
    private router = inject(Router);
    private messageService = inject(MessageService);
    private route = inject(ActivatedRoute);
    // View state is signal-based: the app is zoneless, so plain-field mutations
    // in promise callbacks would never trigger change detection.
    email = signal('');
    password = signal('');
    checked = signal(false);
    mode = signal<'login' | 'forgot'>('login');
    forgotSubmitted = signal(false);
    loading = signal(false);
    loginStep = signal<'email' | 'password'>('email');
    isEmailLinkFlow = signal(false);

    /**
     * RFC 5322 simplified email validation regex
     * Compiled once for performance - handles most common email formats
     */
    private static readonly EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    private static readonly MAX_EMAIL_LENGTH = 254; // RFC 5321 max length

    /**
     * Email validation to prevent storing invalid emails in localStorage.
     * Note: This is a basic sanity check. Full validation happens on the backend.
     */
    private isValidEmail(email: string): boolean {
        if (!email || typeof email !== 'string') return false;
        return CloudLogin.EMAIL_REGEX.test(email) && email.length <= CloudLogin.MAX_EMAIL_LENGTH;
    }

    async ngOnInit(): Promise<void> {
        // Wait for Firebase to initialize
        try {
            await this.firebaseAuth.waitForInit();
        } catch (e) {
            console.error('Firebase init failed:', e);
        }

        // Check if this is an email sign-in link (for invites)
        if (this.firebaseAuth.isEmailSignInLink()) {
            this.isEmailLinkFlow.set(true);
            // Try to get email from localStorage (set when invite was sent)
            const storedEmail = localStorage.getItem('emailForSignIn');
            if (storedEmail && this.isValidEmail(storedEmail)) {
                this.email.set(storedEmail);
                this.handleEmailLinkSignIn(storedEmail);
            } else {
                // Need to ask for email - show email input
                this.loginStep.set('email');
            }
            return;
        }

        // Load last used email from localStorage (for convenience)
        try {
            const lastEmail = localStorage.getItem('lastLoginEmail');
            if (lastEmail && this.isValidEmail(lastEmail)) {
                this.email.set(lastEmail);
            }
        } catch (e) {
            // localStorage may throw in private browsing mode or when quota exceeded
            console.warn('Failed to load last email from localStorage:', e);
        }

        // Check if user is already authenticated via Firebase
        if (this.firebaseAuth.isFirebaseAuthenticated()) {
            this.exchangeFirebaseToken();
        }
    }

    /**
     * Handle sign-in with email link (for invited users)
     */
    private async handleEmailLinkSignIn(email: string): Promise<void> {
        try {
            this.loading.set(true);
            const user = await this.firebaseAuth.signInWithEmailLink(email);
            if (user) {
                // Clear stored email
                localStorage.removeItem('emailForSignIn');
                // Exchange token and navigate
                await this.exchangeFirebaseToken();
            }
        } catch (error: any) {
            console.error('Email link sign-in failed:', error);
            this.loading.set(false);
            // Show error but keep them on login page
        }
    }

    // Check if any social providers are enabled
    hasSocialProviders(): boolean {
        return this.firebaseAuth.isGoogleEnabled() || this.firebaseAuth.isMicrosoftEnabled() || this.firebaseAuth.isSamlEnabled();
    }

    // Helper to get query param from current or parent routes
    private getReturnUrlParam(): string {
        let route: ActivatedRoute | null = this.route;
        while (route) {
            const val = route.snapshot.queryParamMap.get('returnUrl');
            if (val) return val;
            route = route.parent;
        }
        return '/';
    }

    onSubmit() {
        if (this.loading()) return;

        if (this.mode() === 'forgot') {
            this.loading.set(true);
            this.firebaseAuth
                .sendPasswordResetEmail(this.email())
                .then(() => {
                    this.loading.set(false);
                    this.forgotSubmitted.set(true);
                })
                .catch(() => {
                    // Treat errors as success to prevent enumeration
                    this.loading.set(false);
                    this.forgotSubmitted.set(true);
                });
            return;
        }

        // Handle email link sign-in flow (for invites)
        if (this.isEmailLinkFlow()) {
            this.handleEmailLinkSignIn(this.email().trim());
            return;
        }

        // Login mode
        if (this.loginStep() === 'email') {
            // Save email to localStorage for convenience on next login
            const trimmedEmail = this.email().trim();
            if (this.isValidEmail(trimmedEmail)) {
                try {
                    localStorage.setItem('lastLoginEmail', trimmedEmail);
                } catch (e) {
                    // localStorage may throw in private browsing mode or when quota exceeded
                    // This is not critical, so just log and continue
                    console.warn('Failed to save email to localStorage:', e);
                }
            }

            // Check if domain requires SAML authentication
            this.loading.set(true);
            this.cloud
                .checkAuthMethodForEmail(trimmedEmail)
                .then(async (result) => {
                    if (result.authMethod === 'saml' && result.provider) {
                        // Redirect to SAML provider
                        try {
                            const user = await this.firebaseAuth.signInWithSAML(result.provider);
                            if (user) {
                                await this.exchangeFirebaseToken();
                            }
                        } catch (error) {
                            console.error('SAML sign-in failed:', error);
                        }
                    } else {
                        // Move to password step for email/password auth
                        this.loginStep.set('password');
                    }
                })
                .catch((error) => {
                    console.warn('Domain check failed, defaulting to password:', error);
                    // Default to password step if check fails
                    this.loginStep.set('password');
                })
                .finally(() => {
                    this.loading.set(false);
                });
            return;
        }

        // Step 2: Email/Password authentication via Firebase
        if (this.loginStep() === 'password') {
            console.log('Attempting login with', this.email(), '***');
            this.loading.set(true);
            this.firebaseAuth
                .signInWithEmailAndPassword(this.email(), this.password())
                .then(async (user) => {
                    if (user) {
                        await this.exchangeFirebaseToken();
                    }
                    this.loading.set(false);
                })
                .catch((error) => {
                    this.loading.set(false);
                    console.error('Login failed:', error);
                });
        }
    }

    switchToForgot() {
        this.mode.set('forgot');
        this.password.set('');
        this.forgotSubmitted.set(false);
    }

    backToLogin() {
        this.mode.set('login');
        this.loginStep.set('email');
        this.password.set('');
        this.forgotSubmitted.set(false);
    }

    backToEmailStep() {
        this.loginStep.set('email');
        this.password.set('');
        // Keep the email value so user doesn't have to re-enter it
    }

    async loginWithMicrosoft() {
        if (this.loading()) return;
        this.loading.set(true);

        try {
            const user = await this.firebaseAuth.signInWithMicrosoft();
            if (user) {
                await this.exchangeFirebaseToken();
            }
        } catch (error) {
            console.error('Microsoft sign-in failed:', error);
        } finally {
            this.loading.set(false);
        }
    }

    async loginWithGoogle() {
        if (this.loading()) return;
        this.loading.set(true);

        try {
            const user = await this.firebaseAuth.signInWithGoogle();
            if (user) {
                await this.exchangeFirebaseToken();
            }
        } catch (error) {
            console.error('Google sign-in failed:', error);
        } finally {
            this.loading.set(false);
        }
    }

    async loginWithSaml() {
        if (this.loading()) return;
        this.loading.set(true);

        try {
            const user = await this.firebaseAuth.signInWithSAML();
            if (user) {
                await this.exchangeFirebaseToken();
            }
        } catch (error) {
            console.error('SAML sign-in failed:', error);
        } finally {
            this.loading.set(false);
        }
    }

    private async exchangeFirebaseToken() {
        try {
            const idToken = await this.firebaseAuth.getIdToken();
            if (!idToken) {
                console.error('Failed to get Firebase ID token');
                return;
            }

            const returnUrl = this.getReturnUrlParam();
            await this.cloud.loginWithFirebaseToken(idToken, this.checked());
            this.router.navigateByUrl(returnUrl);
        } catch (error) {
            console.error('Failed to exchange Firebase token:', error);
        }
    }
}
